
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useState } from "react";
import { ssrDocument } from "@/utils/ssrUtils";
import AvatarStack from "@/components/ui/AvatarStack";

const HeroSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const scrollToWaitlist = () => {
    const waitlistSection = ssrDocument.getElementById('waitlist-section');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="relative bg-[#050505] py-12 md:py-32">
      <div className="absolute inset-0 bg-[#050505]/50"></div>
      <div className="container px-4 md:px-6 text-center relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between space-y-8 md:space-y-0 md:space-x-12">
          <div className="md:w-1/2 text-left">
            <div className="hidden md:flex items-center mb-4">
              <img
                src="https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Logo%20White&Color.svg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0xvZ28gV2hpdGUmQ29sb3Iuc3ZnIiwiaWF0IjoxNzQ0ODk3NzY5LCJleHAiOjE3NzY0MzM3Njl9.Mi0A6bu8o4zL0eHA01_QaALcR8Qo68Tk7DIxeYKq7Fk"
                alt="CodeRide Logo"
                className="h-16 w-auto mb-4"
                loading="lazy"
              />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight max-w-3xl text-white mb-4">
              The Project Management Tool for Coding Agents using MCP
            </h1>
            <p className="mt-4 text-white/80 max-w-[42rem] text-lg mb-6">
              Upload your project documentation or PRD, CodeRide breaks it into optimized, fully <span className="bg-[#7867EE] text-white px-2 py-1 rounded font-mono">contextual tasks</span> ready for your AI Agent.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="primary"
                size="lg"
                showArrow={true}
                onClick={() => window.open('https://app.coderide.ai/auth/sign-up', '_blank')}
              >
                Get Started for Free
              </Button>
            </div>
            {/* Avatar stack - social proof */}
            <AvatarStack
              className="mt-4 md:mt-6"
              ringColorClass="ring-white/10"
              size="sm"
            />
          </div>
          <div className="md:w-1/2 flex justify-center relative">
            <img
              src="https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/coderide_dashboard-2.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NvZGVyaWRlX2Rhc2hib2FyZC0yLndlYnAiLCJpYXQiOjE3NDUwNjExNTIsImV4cCI6MTc3NjU5NzE1Mn0.fiQCDIjdWtByoU3vFYwTmoFTqCQ7jXN74FMr291bKdY"
              alt="CodeRide Product"
              width={1200}
              height={675}
              sizes="(max-width: 768px) 100vw, 50vw"
              loading="eager"
              style={{
                borderRadius: "0.375rem" // Tailwind md rounded corners
              }}
              className="max-w-full h-auto rounded-md"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Dialog open={isModalOpen} onOpenChange={(open) => {
                setIsModalOpen(open);
                if (open) {
                  setIsLoading(true);
                }
              }}>
                <DialogTrigger asChild>
                  <button 
                    className="flex items-center gap-2 bg-white/90 hover:bg-white text-black px-4 py-2 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium">Watch Demo 3 min</span>
                  </button>
                </DialogTrigger>
                
                <DialogContent className="max-w-7xl w-[98vw] h-[95vh] p-0 border-0 bg-transparent">
                  {/* Full-Screen Loading Overlay */}
                  {isLoading && (
                    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50">
                      {/* Main Content */}
                      <div className="flex flex-col items-center mb-12">
                        <h2 className="text-white text-3xl font-bold mb-3">CodeRide Demo</h2>
                        <p className="text-white/70 text-center max-w-md text-lg">
                          Preparing your interactive experience...
                        </p>
                      </div>

                      {/* Enhanced Progress Section */}
                      <div className="flex flex-col items-center gap-6 w-full max-w-lg px-8">
                        {/* Custom Enhanced Progress Bar */}
                        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden relative">
                          <div 
                            className="h-full rounded-full transition-all duration-700 ease-out relative"
                            style={{
                              width: '75%',
                              backgroundColor: '#53CADE',
                              boxShadow: '0 0 20px #53CADE40'
                            }}
                          >
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                          </div>
                        </div>
                        
                        {/* Progress Text */}
                        <div className="flex justify-between w-full text-sm text-white/60">
                          <span>Almost ready...</span>
                          <span>75%</span>
                        </div>
                      </div>

                      {/* Animated Loading Dots */}
                      <div className="flex gap-2 mt-8">
                        {[0, 1, 2].map((i) => (
                          <div
                            key={i}
                            className="w-2 h-2 bg-coderide-light-blue rounded-full animate-pulse"
                            style={{
                              animationDelay: `${i * 0.3}s`,
                              animationDuration: '1.5s'
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Demo Content */}
                  <iframe
                    src="https://supademo.com/embed/cmde9o7zf00wrzy0i9h1x6qek"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allowFullScreen
                    className="rounded-lg shadow-2xl"
                    onLoad={() => setIsLoading(false)}
                  />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
